import { useLocalStorageState } from 'ahooks';
import { getAuth } from 'firebase/auth';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { get } from 'src/api/requests';
import { EnvironmentSwitcherModal } from 'src/components/EnvironmentSwitcher';
import { logoutHelper } from 'src/helpers/logoutHelper';
import { UserRole } from 'src/hooks/usePermissions';
import { Environment } from 'src/interfaces/environment.interface';
import { getPayloadJWT } from 'src/utils/jwt';
import { useUsersPreferences } from 'src/hooks/useUsersPreferences';

export type EnvironmentContext = {
  userId: string;
  envId: string;
};

type EnvironmentContextType = {
  environment: EnvironmentContext | undefined;
  updateEnvironment: (environment: EnvironmentContext) => void;
  environmentList: Environment[];
  setEnvironmentList: (environments: Environment[]) => void;
  fetchEnvironments: (page?: number, perPage?: number) => Promise<void>;
  environmentData: Environment | undefined;
  role: UserRole;
  hiddenEnvironmentsIds: string[];
};

type EnvironmentProviderProps = {
  children: React.ReactNode;
};

const EnvironmentContext = createContext<EnvironmentContextType>(
  {} as EnvironmentContextType,
);

export const EnvironmentProvider = ({ children }: EnvironmentProviderProps) => {
  const [role, setRole] = useState<UserRole>('member');
  const [environmentList, setEnvironmentList] = useState<Environment[]>([]);
  const [environment, setEnvironment] =
    useLocalStorageState<EnvironmentContext>('@control-plane/environment', {
      defaultValue: {} as EnvironmentContext,
      listenStorageChange: true,
    });
  const environmentData = environmentList.find(
    (env) => env.id === environment?.envId,
  );

  const [hiddenEnvironmentsIds, setHiddenEnvironmentsIds] = useState<string[]>(
    [],
  );

  const auth = getAuth();
  const { getUserPreferences } = useUsersPreferences();

  useEffect(() => {
    const fetchRole = async () => {
      if (!auth.currentUser) {
        setRole('member');
        return;
      }

      try {
        const token = await auth.currentUser.getIdToken();
        const decoded = getPayloadJWT(token);
        const roles = Array.isArray(decoded?.roles) ? decoded.roles : [];

        const isAdmin = roles.some((r) => r?.name === 'Admin');

        setRole(isAdmin ? 'admin' : 'member');
      } catch (error) {
        console.error('Error fetching role:', error);
        setRole('member');
      }
    };

    fetchRole();

    const unsubscribeIdTokenChanged = auth.onIdTokenChanged(() => {
      fetchRole();
    });

    return () => {
      unsubscribeIdTokenChanged();
    };
  }, [auth]);

  const updateEnvironment = (newEnvironment: EnvironmentContext) => {
    setEnvironment(newEnvironment);
  };

  const fetchEnvironments = async (page: number = 0, perPage: number = 10) => {
    const query = new URLSearchParams({
      page: String(page),
      size: String(perPage),
    });

    const data = await get(`/environments?${query.toString()}`, {
      envId: environment?.envId,
    });

    try {
      const userPreferences = await getUserPreferences();
      setHiddenEnvironmentsIds(userPreferences?.hiddenEnvironmentsIds || []);
    } catch (error) {
      console.error('Error fetching user preferences:', error);
      setHiddenEnvironmentsIds([]);
    }

    setEnvironmentList(data);
  };

  if (!environment?.envId && !auth.currentUser) {
    logoutHelper(false);
  }

  if (!environment?.envId && auth.currentUser) {
    return (
      <EnvironmentContext.Provider
        value={{
          environment,
          updateEnvironment,
          environmentList,
          setEnvironmentList,
          fetchEnvironments,
          environmentData,
          role,
          hiddenEnvironmentsIds,
        }}
      >
        <EnvironmentSwitcherModal title="Select environment" modalOpen={true} />
      </EnvironmentContext.Provider>
    );
  }

  return (
    <EnvironmentContext.Provider
      value={{
        environment,
        updateEnvironment,
        environmentList,
        setEnvironmentList,
        fetchEnvironments,
        environmentData,
        role,
        hiddenEnvironmentsIds,
      }}
    >
      {children}
    </EnvironmentContext.Provider>
  );
};

export const useEnvironment = () => {
  return useContext(EnvironmentContext);
};
